// Sprint TENANTS TENANTS.B3 (#2339) — feature-flags section inside the
// Details tab of TenantExpandedRow. Separate file from
// TenantActionsFooter.tsx because these render inline as a section (with
// toggles), not as footer buttons that open a modal — a materially
// different UI shape from the other four actions in this story.
//
// ── Which flags are editable, and why ───────────────────────────────────────
// Mirrors tenantAdminActions.service.ts's EDITABLE_TENANT_FLAGS exactly —
// academic_key_inference_enabled, execution_agent_enabled and (#3745)
// prototype_intake_enabled. The other six flags on GET /:tenantId's feature_flags are read-only here on
// purpose (see that service file's own comment): each already has its own
// dedicated self-service or config-driven path, and PATCH .../flags's Zod
// `.strict()` schema 400s any attempt to send them, naming the key.
//
// ── execution_agent_enabled's asymmetric confirmation gate ──────────────────
// Only ENABLING it (false → true) requires the typed "CONFIRM" step and the
// `X-Blueprint-Confirm` header — mirroring updateTenantFlags's own server-
// side check (`if (flags.executionAgentEnabled === true && !confirmed)`).
// Disabling it fires the same lightweight reason-only flow as
// academic_key_inference_enabled, since the server itself draws the line at
// enabling, not at "any change to this flag."
//
// ── Reason is required even though the story's flags section doesn't show
//    one explicitly — PATCH .../flags's Zod schema requires `reason` (min
//    10 chars) unconditionally, same as every other TENANTS.A3 endpoint.
//    Skipping it here would just mean every toggle 400s.
//
// ── Flag description tooltips (follow-on story) ─────────────────────────────
// R1 correction (#2399): the original version of this comment claimed "no
// reusable tooltip component exists anywhere in apps/web/src/" — that's
// overclaimed. Two reusable popovers do exist: components/help/HelpPopover.tsx
// (documented, ~20 call sites, has a `compact`/`info` mode close to this
// exact shape) and components/TierFeaturesPopover.tsx. Neither fits here:
// HelpPopover.tsx's own README explicitly lists "hover-only triggers" under
// "Out of scope (do not extend the component to support these)" — click is
// its only supported open interaction, by design. The accurate claim is "no
// reusable HOVER-triggered explanation component exists" — reaching for
// HelpPopover and extending it to support hover would go against that
// component's documented intent, not just be extra work.
//
// This codebase's existing hover-explanation convention elsewhere (used in
// this same file, and in TenantExpandedRow.tsx's "View on platform" button)
// is the native `title` attribute. Not used here deliberately: a native
// title tooltip is OS-rendered in a separate compositing layer, so it can't
// be styled for contrast and is never clipped by anything — making this
// story's own "sufficient contrast" / "doesn't get clipped by the row
// boundary" acceptance criteria inapplicable to it. Built a small custom
// hover panel instead (FlagInfoIcon below), using local component state and
// inline styles to match this file's existing 100% inline-style convention
// (no Tailwind classes exist anywhere in the Tenant* files). Confirmed no
// `overflow: hidden` exists anywhere in the ancestor chain up through
// InternalTenantsPage.tsx, so the absolutely-positioned panel has nothing to
// clip against.
//
// Now three small, divergent explanation/popover widgets exist in this
// codebase (TierFeaturesPopover, HelpPopover, this file's FlagInfoIcon) —
// plausibly a consolidation candidate later (e.g. a shared component
// supporting both click and hover triggers), not urgent enough to block
// this PR on.

import { useState } from 'react';
import { Info, Lock } from 'lucide-react';
import { apiFetch, ApiError } from '../../lib/api';

const EDITABLE_FLAGS = ['academic_key_inference_enabled', 'execution_agent_enabled', 'prototype_intake_enabled'] as const;
type EditableFlag = typeof EDITABLE_FLAGS[number];

const EXECUTION_AGENT_CONFIRM_HEADER = 'X-Blueprint-Confirm';
const EXECUTION_AGENT_CONFIRM_VALUE  = 'execution_agent_enabled';
const CONFIRM_TEXT = 'CONFIRM';

// Explicit labels rather than an auto-formatted key — several of these
// (pi_spotlighting, byollm-adjacent naming elsewhere in the app) read badly
// when naively title-cased.
const FLAG_LABELS: Record<string, string> = {
  academic_key_inference_enabled: 'Academic key inference',
  execution_agent_enabled:        'Execution agent',
  managed_agents_enabled:         'Managed agents',
  pi_spotlighting_enabled:        'PI spotlighting',
  prototype_intake_enabled:       'Prototype intake',
  claude_design_export_enabled:   'Claude Design export',
  academic_token_budget_enabled:  'Academic token budget',
  help_agent_enabled:             'Help agent',
  quality_feedback_enabled:       'Quality feedback',
};

// Verbatim from the story prompt — not re-derived from code, since these
// are product-facing explanations of what each flag DOES, not something
// inferable from the flag's own name or column comment.
const FLAG_DESCRIPTIONS: Record<string, string> = {
  academic_key_inference_enabled:
    'Allows the platform to infer the LLM API key from the tenant\'s academic token budget, so students do not need to configure their own API key. Used for academic pilots like the REC engagement.',
  execution_agent_enabled:
    'Enables the execution substrate — the Development agent can autonomously execute code, not just generate it. Zero tenants currently have this enabled. High-risk: requires double confirmation to enable.',
  managed_agents_enabled:
    'Enables fully automated pipeline execution where BluePrint orchestrates the complete SDLC agent chain without manual phase triggers.',
  pi_spotlighting_enabled:
    'Enables personally identifiable information (PII) detection and highlighting in agent outputs. Flags potential PII in generated artefacts before export. For tenants with data sensitivity requirements.',
  // #3745 (R1 P2): since #3711 this flag also gates the Figma read adapter.
  prototype_intake_enabled:
    'Enables the prototype intake workflow — allows a tenant to upload an existing prototype and have BluePrint reverse-engineer it into a structured project with requirements, architecture, and sprint plan. Also gates Figma connect, the Figma allow-list and Figma intake: with this off, none of them is available to the tenant.',
  claude_design_export_enabled:
    'Enables the Claude Design export integration — UX artefacts from Phase 2.5 can be exported to Claude Design for visual prototyping, or imported back from a Claude Design session.',
  academic_token_budget_enabled:
    'Enables the academic token budget system. The tenant draws from a pre-allocated token pool managed by the platform admin, rather than billing directly. Used for academic pilots.',
  help_agent_enabled:
    'Enables the in-app Help Agent — a contextual assistant that answers questions about the platform, the current phase, and best practices while the user is working.',
  // Not in the story's own description list (which covers 8 of the 9
  // flags this section renders) — sourced from schema/tenants.ts's own
  // comment on this column (Sprint 12.5 ACC.B6) rather than left blank or
  // invented, since every OTHER flag on this list has a description.
  quality_feedback_enabled:
    'Enables the per-section quality feedback UI (thumbs icons) on generated artefacts. Opt-in; tenant admins turn it on via Settings → Organisation. Default off.',
};

// Fixed display order — Object.keys on the API's response has no
// guaranteed order the UI should depend on, and a stable order matters for
// an admin scanning this list repeatedly.
const FLAG_ORDER = [
  'academic_key_inference_enabled',
  'execution_agent_enabled',
  'managed_agents_enabled',
  'pi_spotlighting_enabled',
  'prototype_intake_enabled',
  'claude_design_export_enabled',
  'academic_token_budget_enabled',
  'help_agent_enabled',
  'quality_feedback_enabled',
];

function isEditable(flag: string): flag is EditableFlag {
  return (EDITABLE_FLAGS as readonly string[]).includes(flag);
}

function errorMessage(err: unknown, fallback: string): string {
  const e = err as ApiError | null;
  return (e?.body?.error as string | undefined) ?? fallback;
}

const rowStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', fontSize: 13 };
const labelStyle: React.CSSProperties = { flex: 1, color: 'var(--text)' };

// ── Info icon + hover tooltip ────────────────────────────────────────────

function FlagInfoIcon({ flag, description }: { flag: string; description: string }) {
  const [hovered, setHovered] = useState(false);

  return (
    <span
      style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setHovered(true)}
      onBlur={() => setHovered(false)}
    >
      <Info
        size={13}
        tabIndex={0}
        role="button"
        aria-describedby={`flag-tooltip-${flag}`}
        aria-label={`About ${FLAG_LABELS[flag] ?? flag}`}
        data-testid={`flag-info-icon-${flag}`}
        style={{ color: 'var(--text-muted)', cursor: 'help', flexShrink: 0 }}
      />
      {hovered && (
        <div
          role="tooltip"
          id={`flag-tooltip-${flag}`}
          data-testid={`flag-info-tooltip-${flag}`}
          style={{
            position: 'absolute',
            bottom: '100%',
            left: '50%',
            transform: 'translateX(-50%)',
            marginBottom: 6,
            width: 260,
            padding: '8px 10px',
            fontSize: 11,
            lineHeight: 1.4,
            color: 'var(--text)',
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 6,
            boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
            zIndex: 20,
            whiteSpace: 'normal',
          }}
        >
          {description}
        </div>
      )}
    </span>
  );
}

// ── Reason-only confirmation (academic_key_inference, prototype_intake, and disabling execution_agent) ──

function ReasonModal({ title, onClose, onConfirm }: { title: string; onClose: () => void; onConfirm: (reason: string) => Promise<void> }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const valid = reason.trim().length >= 10;

  async function handleSubmit() {
    if (!valid || busy) return;
    setBusy(true);
    setError('');
    try {
      await onConfirm(reason);
    } catch (err) {
      setError(errorMessage(err, 'Failed to update flag.'));
      setBusy(false);
    }
  }

  return (
    <div
      role="dialog" aria-modal="true" aria-label={title} data-testid="flag-reason-modal"
      style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', background: 'rgba(0,0,0,0.45)', padding: '48px 16px', overflowY: 'auto' }}
      onClick={onClose}
    >
      <div onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: 420, background: 'var(--bg)', borderRadius: 8, border: '1px solid var(--border)', boxShadow: '0 12px 40px rgba(0,0,0,0.3)', padding: 20 }}>
        <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 15, fontWeight: 600, color: 'var(--text)', margin: '0 0 12px' }}>{title}</h3>
        <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 }} htmlFor="flag-reason">Reason (minimum 10 characters)</label>
        <textarea
          id="flag-reason" value={reason} onChange={e => setReason(e.target.value)} rows={3} data-testid="flag-reason-input"
          style={{ width: '100%', padding: '6px 10px', fontSize: 13, border: '1px solid var(--border)', borderRadius: 6, background: 'var(--surface)', color: 'var(--text)', boxSizing: 'border-box', resize: 'vertical' }}
        />
        {error && <p role="alert" style={{ fontSize: 12, color: 'var(--error)', marginTop: 8 }}>{error}</p>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <button type="button" onClick={onClose} style={{ fontSize: 13, padding: '6px 14px', background: 'transparent', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text)', cursor: 'pointer' }}>Cancel</button>
          <button
            type="button" onClick={() => void handleSubmit()} disabled={busy || !valid} data-testid="flag-reason-submit"
            style={{ fontSize: 13, fontWeight: 600, padding: '6px 14px', color: 'white', border: 'none', borderRadius: 6, background: busy || !valid ? 'var(--text-muted)' : 'var(--primary, #6366f1)', cursor: busy || !valid ? 'not-allowed' : 'pointer' }}
          >
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Typed-CONFIRM gate for enabling execution_agent_enabled ─────────────────

function ExecutionAgentConfirmModal({ onClose, onConfirm }: { onClose: () => void; onConfirm: (reason: string) => Promise<void> }) {
  const [reason, setReason] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const valid = reason.trim().length >= 10 && confirmText === CONFIRM_TEXT;

  async function handleSubmit() {
    if (!valid || busy) return;
    setBusy(true);
    setError('');
    try {
      await onConfirm(reason);
    } catch (err) {
      setError(errorMessage(err, 'Failed to enable execution agent.'));
      setBusy(false);
    }
  }

  return (
    <div
      role="dialog" aria-modal="true" aria-label="Enable execution agent" data-testid="execution-agent-confirm-modal"
      style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', background: 'rgba(0,0,0,0.45)', padding: '48px 16px', overflowY: 'auto' }}
      onClick={onClose}
    >
      <div onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: 440, background: 'var(--bg)', borderRadius: 8, border: '1px solid var(--border)', boxShadow: '0 12px 40px rgba(0,0,0,0.3)', padding: 20 }}>
        <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 15, fontWeight: 600, color: 'var(--text)', margin: '0 0 8px' }}>Enable execution agent</h3>
        <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '0 0 12px' }}>
          This grants the tenant autonomous execution capability. This action requires explicit confirmation.
        </p>
        <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 }} htmlFor="exec-agent-reason">Reason (minimum 10 characters)</label>
        <textarea
          id="exec-agent-reason" value={reason} onChange={e => setReason(e.target.value)} rows={3} data-testid="execution-agent-reason"
          style={{ width: '100%', padding: '6px 10px', fontSize: 13, border: '1px solid var(--border)', borderRadius: 6, background: 'var(--surface)', color: 'var(--text)', boxSizing: 'border-box', resize: 'vertical' }}
        />
        <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4, marginTop: 12 }} htmlFor="exec-agent-confirm-text">
          Type <strong>{CONFIRM_TEXT}</strong> to confirm
        </label>
        <input
          id="exec-agent-confirm-text" type="text" value={confirmText} onChange={e => setConfirmText(e.target.value)} data-testid="execution-agent-confirm-text"
          style={{ width: '100%', padding: '6px 10px', fontSize: 13, border: '1px solid var(--border)', borderRadius: 6, background: 'var(--surface)', color: 'var(--text)', boxSizing: 'border-box' }}
        />
        {error && <p role="alert" style={{ fontSize: 12, color: 'var(--error)', marginTop: 8 }}>{error}</p>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <button type="button" onClick={onClose} style={{ fontSize: 13, padding: '6px 14px', background: 'transparent', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text)', cursor: 'pointer' }}>Cancel</button>
          <button
            type="button" onClick={() => void handleSubmit()} disabled={busy || !valid} data-testid="execution-agent-confirm-submit"
            style={{ fontSize: 13, fontWeight: 600, padding: '6px 14px', color: 'white', border: 'none', borderRadius: 6, background: busy || !valid ? 'var(--text-muted)' : 'var(--error)', cursor: busy || !valid ? 'not-allowed' : 'pointer' }}
          >
            {busy ? 'Enabling…' : 'Enable'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Component ───────────────────────────────────────────────────────────────

interface Props {
  tenantId: string;
  flags: Record<string, boolean>;
  onActionSuccess: () => void;
}

export default function TenantFeatureFlagsSection({ tenantId, flags, onActionSuccess }: Props) {
  const [pendingFlag, setPendingFlag] = useState<EditableFlag | null>(null);
  const [pendingValue, setPendingValue] = useState(false);
  const [toast, setToast] = useState('');

  async function patchFlag(flag: EditableFlag, value: boolean, reason: string) {
    const confirmed = flag === 'execution_agent_enabled' && value === true;
    await apiFetch(`/api/internal/tenants/${encodeURIComponent(tenantId)}/flags`, {
      method: 'PATCH',
      body: JSON.stringify({ [flag]: value, reason }),
      headers: confirmed ? { [EXECUTION_AGENT_CONFIRM_HEADER]: EXECUTION_AGENT_CONFIRM_VALUE } : undefined,
    });
    setPendingFlag(null);
    setToast(`${FLAG_LABELS[flag]} updated.`);
    onActionSuccess();
  }

  // pendingValue is captured from the click itself, not re-read from
  // `flags` later — the toggle's post-click state, not its pre-click one.
  function handleToggle(flag: EditableFlag, nextValue: boolean) {
    setPendingFlag(flag);
    setPendingValue(nextValue);
  }

  return (
    <div>
      {toast && <div data-testid="flags-toast" role="status" style={{ fontSize: 12, color: 'var(--success, #10b981)', marginBottom: 8 }}>{toast}</div>}

      {FLAG_ORDER.filter(f => f in flags).map(flag => {
        const value = flags[flag];
        const editable = isEditable(flag);
        return (
          <div key={flag} style={rowStyle} data-testid={`flag-row-${flag}`}>
            <span style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 6 }}>
              {FLAG_LABELS[flag] ?? flag}
              <FlagInfoIcon flag={flag} description={FLAG_DESCRIPTIONS[flag] ?? 'No description available.'} />
            </span>
            {editable ? (
              <input
                type="checkbox"
                role="switch"
                checked={value}
                onChange={e => handleToggle(flag, e.target.checked)}
                data-testid={`flag-toggle-${flag}`}
                aria-label={`Toggle ${FLAG_LABELS[flag] ?? flag}`}
              />
            ) : (
              <span title="Managed via configuration" data-testid={`flag-lock-${flag}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--text-muted)', fontSize: 11 }}>
                <Lock size={12} />
                {value ? 'On' : 'Off'}
              </span>
            )}
          </div>
        );
      })}

      {pendingFlag === 'execution_agent_enabled' && pendingValue && (
        <ExecutionAgentConfirmModal
          onClose={() => setPendingFlag(null)}
          onConfirm={(reason) => patchFlag('execution_agent_enabled', true, reason)}
        />
      )}
      {pendingFlag !== null && !(pendingFlag === 'execution_agent_enabled' && pendingValue) && (
        <ReasonModal
          title={`${pendingValue ? 'Enable' : 'Disable'} ${FLAG_LABELS[pendingFlag]}`}
          onClose={() => setPendingFlag(null)}
          onConfirm={(reason) => patchFlag(pendingFlag, pendingValue, reason)}
        />
      )}
    </div>
  );
}
